import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AppPage } from '@/components/layout/AppPage';
import { API } from '@/lib/api';
import { useViewportSize } from '@/hooks/useViewportSize';
import { buildHtmlExport, buildPlainTextExport, resolveShareUrl, triggerDownload } from '@/lib/shareExport';
import { toast } from 'sonner';
import {
  Bookmark, Trash2, ExternalLink, MessageSquare, FileText, Search as SearchIcon, Scale,
  BookOpen, Bot, Building2, GraduationCap, User, FolderOpen, Users, Filter, Download, Share2,
} from 'lucide-react';

const TYPE_TABS = ['All', 'CHAT', 'DOCUMENT', 'RESEARCH', 'CASE_LAW', 'JUDGMENT'];
const TYPE_HINTS = {
  All: 'Everything you have saved across chats, documents, research, case law, and judgment tracking.',
  CHAT: 'Saved chat takeaways, assistant outputs, and reusable conversational work product.',
  DOCUMENT: 'Drafts, document notes, and saved writing connected to a legal matter or project.',
  RESEARCH: 'Long-form research reports, topic notes, case analysis, and research-agent outputs.',
  CASE_LAW: 'Individual cases, authorities, and judgments you bookmarked for later reference.',
  JUDGMENT: 'Daily brief items and judgment monitoring saves for ongoing awareness.',
};
const WORKSPACE_FILTERS = [
  { value: 'ALL', label: 'All Workspaces' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'FIRM', label: 'Firm' },
  { value: 'CLASS', label: 'Class' },
  { value: 'SHARED_WITH_ME', label: 'Shared With Me' },
];
const WORKSPACE_HINTS = {
  ALL: 'See every saved item regardless of whether it belongs to your own work, class work, or a shared firm workspace.',
  PERSONAL: 'Private notes, personal research, and work that belongs only to your own account.',
  FIRM: 'Matter folders and research grouped under your firm or chambers so teammates can stay aligned.',
  CLASS: 'Class notes, exam prep, moot work, and law school research grouped together.',
  SHARED_WITH_ME: 'Items saved by other members of your shared workspace that you can review and continue.',
};

const typeIcons = {
  CHAT: MessageSquare,
  DOCUMENT: FileText,
  RESEARCH: SearchIcon,
  CASE_LAW: Scale,
  JUDGMENT: Scale,
};

const workspaceMeta = {
  PERSONAL: { label: 'Personal', icon: User },
  FIRM: { label: 'Firm', icon: Building2 },
  CLASS: { label: 'Class', icon: GraduationCap },
};

const activityLabels = {
  DRAFTING: 'Drafting',
  RESEARCH: 'Research',
  CASE_RESEARCH: 'Case Research',
  CASE_ANALYSIS: 'Case Analysis',
  STATUTORY_RESEARCH: 'Statute Research',
  PROFILE_RESEARCH: 'Profile Research',
  STUDY: 'Study',
  MONITORING: 'Monitoring',
  REFERENCE: 'Reference',
};

function cleanHtml(html) {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch {
    return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  }
}

function formatPreviewText(content) {
  return cleanHtml(content)
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/\n{2,}/g, '\n')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatLabel(value) {
  return String(value || '')
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function capitalizeLeadingAlpha(value) {
  return String(value || '').replace(/[A-Za-z]/, char => char.toUpperCase());
}

function matchesWorkspaceFilter(item, filter) {
  if (filter === 'ALL') return true;
  if (filter === 'SHARED_WITH_ME') return Boolean(item.shared_with_me);
  return (item.workspace?.workspace_type || 'PERSONAL') === filter;
}

function buildWorkspaceSections(items) {
  const sections = new Map();

  items.forEach(item => {
    const workspace = item.workspace || {};
    const workspaceType = workspace.workspace_type || 'PERSONAL';
    const workspaceKey = `${workspaceType}:${workspace.workspace_key || item.user_id || 'workspace'}`;
    const projectKey = workspace.project_key || item.item_id;

    if (!sections.has(workspaceKey)) {
      sections.set(workspaceKey, {
        key: workspaceKey,
        workspaceType,
        workspaceName: workspace.workspace_name || 'Workspace',
        sharedCount: 0,
        itemCount: 0,
        latestAt: item.created_at,
        projects: new Map(),
      });
    }

    const section = sections.get(workspaceKey);
    section.itemCount += 1;
    if (item.shared_with_me) section.sharedCount += 1;
    if (item.created_at > section.latestAt) section.latestAt = item.created_at;

    if (!section.projects.has(projectKey)) {
      section.projects.set(projectKey, {
        key: `${workspaceKey}:${projectKey}`,
        projectName: capitalizeLeadingAlpha(workspace.project_name || item.title),
        projectType: workspace.project_type || 'REFERENCE',
        caseName: capitalizeLeadingAlpha(workspace.case_name || ''),
        activityType: workspace.activity_type || 'REFERENCE',
        latestAt: item.created_at,
        items: [],
      });
    }

    const project = section.projects.get(projectKey);
    project.items.push(item);
    if (item.created_at > project.latestAt) project.latestAt = item.created_at;
  });

  return [...sections.values()]
    .map(section => ({
      ...section,
      projects: [...section.projects.values()]
        .sort((a, b) => (b.latestAt || '').localeCompare(a.latestAt || ''))
        .map(project => ({
          ...project,
          items: [...project.items].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
        })),
    }))
    .sort((a, b) => (b.latestAt || '').localeCompare(a.latestAt || ''));
}

export default function SavedPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { width, height } = useViewportSize();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('All');
  const [workspaceFilter, setWorkspaceFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [landingExpanded, setLandingExpanded] = useState(false);

  useEffect(() => { fetchSaved(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSaved = async () => {
    try {
      const resp = await fetch(`${API}/saved`, { headers: getAuthHeaders(), credentials: 'include' });
      if (resp.ok) setItems(await resp.json());
    } catch {}
  };

  const deleteItem = async (itemId) => {
    try {
      await fetch(`${API}/saved/${itemId}`, { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' });
      setItems(prev => prev.filter(item => item.item_id !== itemId));
      toast.success('Item moved to trash');
    } catch {}
  };

  const shareItem = async (item) => {
    try {
      const resp = await fetch(`${API}/share-links`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ entity_type: 'SAVED_ITEM', entity_id: item.item_id }),
      });
      if (!resp.ok) throw new Error('Share link failed');
      const payload = await resp.json();
      const shareUrl = resolveShareUrl(payload, window.location.origin);
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied');
    } catch {
      toast.error('Failed to create share link');
    }
  };

  const exportItem = (item, format) => {
    const payload = format === 'html'
      ? buildHtmlExport({ title: item.title, content: item.content, author: user?.name || '', sourceLabel: 'Advoq saved output' })
      : buildPlainTextExport({ title: item.title, content: item.content, sourceLabel: 'Advoq saved output' });
    triggerDownload(payload);
    toast.success(format === 'html' ? 'HTML export ready' : 'Text export ready');
  };

  const openItem = (item) => {
    if (item.agent_type === 'RESEARCH_AGENT') {
      navigate(`/research?saved_item_id=${item.item_id}`);
      return;
    }
    const params = new URLSearchParams({
      linked_doc_id: item.item_id,
      linked_doc_type: 'saved_item',
      linked_doc_title: capitalizeLeadingAlpha(item.title),
    });
    navigate(`/chat?${params.toString()}`);
  };

  const normalizedSearch = search.trim().toLowerCase();
  const isCompactViewport = width < 1180 || height < 900;
  const landingSectionLimit = 1;
  const landingProjectLimit = isCompactViewport ? 1 : 2;
  const landingItemLimit = height < 860 ? 1 : 2;
  const filteredItems = items.filter(item => {
    if (tab !== 'All' && item.type !== tab) return false;
    if (!matchesWorkspaceFilter(item, workspaceFilter)) return false;
    if (!normalizedSearch) return true;
    const haystack = [
      item.title,
      cleanHtml(item.content),
      item.workspace?.project_name,
      item.workspace?.case_name,
      item.workspace?.workspace_name,
      item.workspace?.owner_name,
      ...(item.tags || []),
    ].join(' ').toLowerCase();
    return haystack.includes(normalizedSearch);
  });

  const sections = buildWorkspaceSections(filteredItems);
  const personalCount = items.filter(item => item.workspace?.workspace_type === 'PERSONAL').length;
  const sharedCount = items.filter(item => item.shared_with_me).length;
  const projectCount = sections.reduce((count, section) => count + section.projects.length, 0);
  const isLandingCollapsed = !landingExpanded && !normalizedSearch && tab === 'All' && workspaceFilter === 'ALL';
  const visibleSections = isLandingCollapsed ? sections.slice(0, landingSectionLimit) : sections;
  const hiddenWorkspaceCount = Math.max(0, sections.length - visibleSections.length);
  const hiddenSavedCount = Math.max(0, filteredItems.length - visibleSections.reduce((count, section) => {
    const visibleProjects = isLandingCollapsed ? section.projects.slice(0, landingProjectLimit) : section.projects;
    return count + visibleProjects.reduce((projectCountSum, project) => {
      const visibleProjectItems = isLandingCollapsed ? project.items.slice(0, landingItemLimit) : project.items;
      return projectCountSum + visibleProjectItems.length;
    }, 0);
  }, 0));
  const activeSummary = `${TYPE_HINTS[tab]} ${WORKSPACE_HINTS[workspaceFilter]}`;
  const expandLanding = () => setLandingExpanded(true);

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" data-testid="saved-page" style={{ height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AppPage
          width="narrow"
          className={`app-page--landing-locked ${isLandingCollapsed ? 'app-page--landing-collapsed' : 'app-page--landing-expanded'}`}
        >
          <div className="advoq-card p-5 sm:p-6">
            <div className="landing-toolbar">
              {isLandingCollapsed && filteredItems.length > 0 ? (
                <div className="landing-toolbar__header">
                  <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                    Open full library
                  </button>
                </div>
              ) : null}

              <div className="landing-overview-grid">
                <div className="landing-overview-card">
                  <span className="landing-overview-card__eyebrow">Projects</span>
                  <span className="landing-overview-card__value">{projectCount}</span>
                  <p className="landing-overview-card__note">Grouped by workspace and activity so the desk lands with context, not clutter.</p>
                </div>
                <div className="landing-overview-card">
                  <span className="landing-overview-card__eyebrow">Personal</span>
                  <span className="landing-overview-card__value">{personalCount}</span>
                  <p className="landing-overview-card__note">Private items that belong only to your own chamber flow.</p>
                </div>
                <div className="landing-overview-card">
                  <span className="landing-overview-card__eyebrow">Shared With You</span>
                  <span className="landing-overview-card__value">{sharedCount}</span>
                  <p className="landing-overview-card__note">External or team items you can continue without leaving this surface.</p>
                </div>
              </div>

              <div className="relative">
                <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  value={search}
                  onFocus={expandLanding}
                  onChange={e => {
                    setSearch(e.target.value);
                    if (!landingExpanded) setLandingExpanded(true);
                  }}
                  className="advoq-input w-full pl-10"
                  placeholder="Search by project, case name, owner, or content"
                />
              </div>

              <div className="landing-filter-stack">
                <div className="landing-filter-row">
                  <span className="landing-filter-label">
                    <Filter size={12} /> Item Type
                  </span>
                  <div className="landing-filter-options">
                    {TYPE_TABS.map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setTab(type);
                          expandLanding();
                        }}
                        className={`landing-filter-option ${tab === type ? 'is-active' : ''}`}
                      >
                        {type === 'All' ? 'All' : type.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="landing-filter-row">
                  <span className="landing-filter-label">
                    <Users size={12} /> Workspace
                  </span>
                  <div className="landing-filter-options">
                    {WORKSPACE_FILTERS.map(filter => (
                      <button
                        key={filter.value}
                        type="button"
                        onClick={() => {
                          setWorkspaceFilter(filter.value);
                          expandLanding();
                        }}
                        className={`landing-filter-option ${workspaceFilter === filter.value ? 'is-active' : ''}`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="landing-supporting-copy">
                <p className="landing-supporting-copy__summary">{activeSummary}</p>
                <span className="landing-toolbar__hint">
                  {isLandingCollapsed ? 'Landing stays fixed until you interact' : 'Full library expanded'}
                </span>
              </div>
            </div>
          </div>

          {sections.length === 0 ? (
            <div className="text-center py-16">
              <Bookmark size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                {items.length ? 'No saved items match these filters' : 'No saved items yet'}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {items.length
                  ? 'Try another workspace or search term.'
                  : 'Save chats, cases, and research from anywhere in the app to build project libraries here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {isLandingCollapsed && (hiddenWorkspaceCount > 0 || hiddenSavedCount > 0) && (
                <div className="landing-collapsed-banner">
                  <p className="landing-collapsed-banner__copy">
                    <strong>Calm first view.</strong> Showing the workspaces and saved items that fit on landing. Open the full library to browse the remaining {hiddenWorkspaceCount > 0 ? `${hiddenWorkspaceCount} workspaces` : `${hiddenSavedCount} saved items`}.
                  </p>
                  <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                    Show everything
                  </button>
                </div>
              )}

              {visibleSections.map(section => {
                const WorkspaceIcon = workspaceMeta[section.workspaceType]?.icon || FolderOpen;
                const workspaceLabel = workspaceMeta[section.workspaceType]?.label || formatLabel(section.workspaceType);
                const visibleProjects = isLandingCollapsed ? section.projects.slice(0, landingProjectLimit) : section.projects;
                const hiddenProjects = Math.max(0, section.projects.length - visibleProjects.length);

                return (
                  <section key={section.key} className="advoq-card p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                            <WorkspaceIcon size={12} />
                            {workspaceLabel}
                          </span>
                          <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                            {section.sharedCount ? `${section.sharedCount} shared with you` : 'Private workspace view'}
                          </span>
                        </div>
                        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                          {capitalizeLeadingAlpha(section.workspaceName)}
                        </h2>
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                          {section.projects.length} project{section.projects.length === 1 ? '' : 's'} grouped in this workspace
                        </p>
                      </div>
                      <p className="text-xs shrink-0" style={{ color: 'var(--text-muted)' }}>
                        Updated {new Date(section.latestAt).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="grid gap-4 mt-4 lg:grid-cols-2">
                      {visibleProjects.map(project => {
                        const visibleProjectItems = isLandingCollapsed ? project.items.slice(0, landingItemLimit) : project.items;
                        const hiddenProjectItems = Math.max(0, project.items.length - visibleProjectItems.length);

                        return (
                        <div
                          key={project.key}
                          className="app-saved-project-card rounded-[24px] p-4 h-full flex flex-col"
                        >
                          <div className="relative flex flex-col gap-3 h-full">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  <span className="text-[11px] px-2 py-1 rounded-full" style={{ background: 'color-mix(in srgb, var(--bg-surface) 76%, transparent)', color: 'var(--text-muted)', border: '1px solid var(--app-panel-border)' }}>
                                    {formatLabel(project.projectType)}
                                  </span>
                                  <span className="text-[11px] px-2 py-1 rounded-full" style={{ background: 'color-mix(in srgb, var(--bg-surface) 76%, transparent)', color: 'var(--text-muted)', border: '1px solid var(--app-panel-border)' }}>
                                    {activityLabels[project.activityType] || formatLabel(project.activityType)}
                                  </span>
                                </div>
                                <h3
                                  className="text-lg font-semibold line-clamp-2 leading-tight pr-3"
                                  style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)', minHeight: '2.8rem' }}
                                >
                                  {capitalizeLeadingAlpha(project.projectName)}
                                </h3>
                                {project.caseName && (
                                  <p className="text-xs mt-1 line-clamp-1" style={{ color: 'var(--accent-primary)' }}>
                                    {capitalizeLeadingAlpha(project.caseName)}
                                  </p>
                                )}
                              </div>
                              <div
                                className="shrink-0 rounded-2xl px-3 py-2 text-right"
                                style={{ background: 'color-mix(in srgb, var(--bg-surface) 76%, transparent)', border: '1px solid var(--app-panel-border)' }}
                              >
                                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Items</p>
                                <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                                  {project.items.length}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center justify-between gap-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              <span>Last activity {new Date(project.latestAt).toLocaleDateString()}</span>
                              <span>{project.items.length === 1 ? 'Single saved item' : 'Compact project view'}</span>
                            </div>

                            <div
                              className="grid gap-2.5 mt-1 flex-1"
                              style={{ overflow: isLandingCollapsed ? 'hidden' : 'auto', maxHeight: isLandingCollapsed ? 'none' : '23rem', paddingRight: isLandingCollapsed ? 0 : '0.25rem' }}
                            >
                            {visibleProjectItems.map(item => {
                              const Icon = typeIcons[item.type] || Bookmark;
                              return (
                                <div
                                  key={item.item_id}
                                  className="app-saved-item-card rounded-2xl p-3"
                                  data-testid={`saved-item-${item.item_id}`}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openItem(item)}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex items-start gap-2.5 min-w-0">
                                      <Icon size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                                      <div className="min-w-0">
                                        <h4 className="text-sm font-semibold line-clamp-2 leading-snug" style={{ color: 'var(--text-primary)', minHeight: '2.4rem' }}>{capitalizeLeadingAlpha(item.title)}</h4>
                                        <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                                          {new Date(item.created_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    {item.is_owner && (
                                      <button onClick={e => { e.stopPropagation(); deleteItem(item.item_id); }} className="p-1 rounded hover:opacity-70 shrink-0">
                                        <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
                                      </button>
                                    )}
                                  </div>

                                  <p className="text-xs mt-2.5 leading-relaxed" style={{ color: 'var(--text-secondary)', display: '-webkit-box', WebkitLineClamp: isLandingCollapsed ? 2 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: isLandingCollapsed ? '2.5rem' : '3rem' }}>
                                    {formatPreviewText(item.content) || 'No preview available.'}
                                  </p>

                                  <div className="flex items-center gap-1.5 flex-wrap mt-3" onClick={e => e.stopPropagation()}>
                                    <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                                      {item.agent_type === 'RESEARCH_AGENT' ? 'Research Workspace' : item.type?.replace(/_/g, ' ')}
                                    </span>
                                    {item.shared_with_me && (
                                      <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}>
                                        Shared by {item.workspace?.owner_name}
                                      </span>
                                    )}
                                    {!item.shared_with_me && (
                                      <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                                        {item.is_owner ? 'Your item' : `Saved by ${item.workspace?.owner_name}`}
                                      </span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-3 flex-wrap mt-3" onClick={e => e.stopPropagation()}>
                                    {item.url && (
                                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[11px] flex items-center gap-1" style={{ color: 'var(--accent-primary)' }}>
                                        Open <ExternalLink size={10} />
                                      </a>
                                    )}
                                    {item.agent_type === 'RESEARCH_AGENT' ? (
                                      <button
                                        onClick={() => navigate(`/research?saved_item_id=${item.item_id}`)}
                                        className="text-[11px] flex items-center gap-1 transition-colors"
                                        style={{ color: 'var(--text-muted)' }}
                                        title="Open in Research"
                                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                      >
                                        <Bot size={11} /> Research
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => openItem(item)}
                                        className="text-[11px] flex items-center gap-1 transition-colors"
                                        style={{ color: 'var(--text-muted)' }}
                                        title="Open in Chat"
                                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                      >
                                        <MessageSquare size={11} /> Chat
                                      </button>
                                    )}
                                    {item.type === 'RESEARCH' && item.research_data && item.agent_type !== 'RESEARCH_AGENT' && (
                                      <button
                                        onClick={() => {
                                          const rd = item.research_data;
                                          const params = new URLSearchParams({
                                            linked_doc_id: item.item_id,
                                            linked_doc_type: 'saved_item',
                                            linked_doc_title: capitalizeLeadingAlpha(item.title),
                                            prefill_query: rd.query || capitalizeLeadingAlpha(item.title),
                                            prefill_type: rd.type || 'TOPIC',
                                          });
                                          navigate(`/research?${params.toString()}`);
                                        }}
                                        className="text-[11px] flex items-center gap-1 transition-colors"
                                        style={{ color: 'var(--text-muted)' }}
                                        title="Continue Research"
                                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                      >
                                        <BookOpen size={11} /> Research
                                      </button>
                                    )}
                                    <button
                                      onClick={() => shareItem(item)}
                                      className="text-[11px] flex items-center gap-1 transition-colors"
                                      style={{ color: 'var(--text-muted)' }}
                                      title="Copy share link"
                                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                    >
                                      <Share2 size={11} /> Share
                                    </button>
                                    <button
                                      onClick={() => exportItem(item, 'txt')}
                                      className="text-[11px] flex items-center gap-1 transition-colors"
                                      style={{ color: 'var(--text-muted)' }}
                                      title="Export text"
                                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-primary)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                                    >
                                      <Download size={11} /> Export
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {isLandingCollapsed && hiddenProjectItems > 0 && (
                              <button
                                type="button"
                                className="rounded-2xl px-3 py-3 text-left text-xs font-medium"
                                style={{ background: 'var(--bg-elevated)', color: 'var(--accent-primary)', border: '1px dashed color-mix(in srgb, var(--accent-primary) 26%, var(--border-default))' }}
                                onClick={expandLanding}
                              >
                                Show {hiddenProjectItems} more item{hiddenProjectItems === 1 ? '' : 's'} in {capitalizeLeadingAlpha(project.projectName)}
                              </button>
                            )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    {isLandingCollapsed && hiddenProjects > 0 && (
                      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {hiddenProjects} more project{hiddenProjects === 1 ? '' : 's'} are tucked away until you expand this library.
                        </p>
                        <button type="button" className="advoq-btn-secondary text-sm" onClick={expandLanding}>
                          Open workspace
                        </button>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </AppPage>
      </main>
    </div>
  );
}
